import { API_URL } from '../utils/api';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import {
  ClipboardText,
  User,
  Buildings,
  GraduationCap,
  Storefront,
} from '@phosphor-icons/react';
import { DataToolbar } from '../components/DataToolbar';
import { DataTable } from '../components/DataTable';

export default function AuditTrailPage() {
  const { isSuperAdmin, isEntityAdmin } = useAuth();
  const { entities } = useTenant();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterEntity, setFilterEntity] = useState('all');
  const [filterResource, setFilterResource] = useState('all');

  const fetchLogs = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let url = `${API_URL}/api/audit-logs`;
      const params = [];
      if (filterEntity && filterEntity !== 'all') params.push(`entity_id=${filterEntity}`);
      if (filterResource && filterResource !== 'all') params.push(`resource_type=${filterResource}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      const { data } = await axios.get(url, { withCredentials: true, headers });
      setLogs(data);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  }, [filterEntity, filterResource]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const filteredLogs = logs.filter((log) =>
    (log.username || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (log.details || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (log.action || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const actionStyle = (action) => {
    if (action === 'CREATE') return 'text-emerald-700 bg-emerald-500/10';
    if (action === 'UPDATE') return 'text-[#0A2540] bg-[#0A2540]/10';
    if (action === 'DELETE') return 'text-rose-700 bg-rose-500/10';
    if (action === 'LOGIN')  return 'text-violet-700 bg-violet-500/10';
    return 'text-slate-700 bg-slate-200/60';
  };

  const ResourceIcon = ({ type }) => {
    if (type === 'user') return <User size={12} />;
    if (type === 'entity') return <Buildings size={12} />;
    if (type === 'campus') return <GraduationCap size={12} />;
    if (type === 'store') return <Storefront size={12} />;
    return <ClipboardText size={12} />;
  };

  if (!isSuperAdmin && !isEntityAdmin) {
    return (
      <div className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-lg p-8 text-center">
        <p className="text-[#78716C]">Access denied. Admin privileges required.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 pb-6" data-testid="audit-trail-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-[#1C1917] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Audit Trail
        </h1>
      </div>

      <DataToolbar
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search by user, action or details…"
        testId="audit-toolbar"
        onClearAll={() => { setSearchQuery(''); setFilterEntity('all'); setFilterResource('all'); }}
        filters={[
          ...(isSuperAdmin ? [{
            key: 'entity',
            label: 'Entity',
            value: filterEntity,
            onChange: setFilterEntity,
            options: [{ value: 'all', label: 'All entities' }, ...entities.map((e) => ({ value: e.id, label: e.entity_name }))],
          }] : []),
          {
            key: 'resource',
            label: 'Resource',
            value: filterResource,
            onChange: setFilterResource,
            options: [
              { value: 'all', label: 'All resources' },
              { value: 'user', label: 'Users' },
              { value: 'entity', label: 'Entities' },
              { value: 'campus', label: 'Campuses' },
              { value: 'store', label: 'Stores' },
              { value: 'subscriber', label: 'Subscribers' },
            ],
          },
        ]}
      />

      <DataTable
        columns={[
          { key: 'created_at', label: 'Timestamp', width: 'minmax(150px,1.3fr)', mono: true, tone: 'muted',
            render: (l) => new Date(l.created_at).toLocaleString() },
          { key: 'username', label: 'User', width: 'minmax(140px,1.2fr)', mono: true, tone: 'strong', render: (l) => `@${l.username}` },
          { key: 'action', label: 'Action', width: 'minmax(110px,1fr)', render: (l) => (
            <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full ${actionStyle(l.action)} group-hover:bg-[#EDE6D8] group-hover:text-[#1C1917]`}>
              {l.action}
            </span>
          ) },
          { key: 'resource_type', label: 'Resource', width: 'minmax(120px,1fr)', render: (l) => (
            <span className="inline-flex items-center gap-1.5 text-xs text-[#1C1917] group-hover:text-[#1C1917] capitalize" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              <ResourceIcon type={l.resource_type} />
              {l.resource_type}
            </span>
          ) },
          { key: 'details', label: 'Details', width: 'minmax(220px,2fr)', tone: 'muted' },
          { key: 'ip_address', label: 'IP', width: 'minmax(120px,1fr)', mono: true, tone: 'muted' },
        ]}
        rows={filteredLogs}
        loading={loading}
        rowKey={(l) => l.id}
        emptyIcon={<ClipboardText size={20} className="text-slate-500" />}
        emptyTitle="No audit logs"
        emptyText={searchQuery ? 'Try a different search term.' : 'Activity will be recorded here.'}
        testIdPrefix="audit"
      />
    </div>
  );
}
